'use client';

import { useCallback, useEffect, useState } from 'react';

import { usePageviewId } from '@packages/tracking/src/components/PageviewProvider/PageviewProvider';
import {
  useCookies,
  type CookieOptions,
} from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useIsHydrated } from '@packages/shared/src/hooks/useIsHydrated/useIsHydrated';

export const appBannerCookieName = 'bannerDisabled';
export const appBannerPageViewsCookieName = 'appBannerPageViews';

const SECONDS_PER_DAY = 86400;

const BASE_COOKIE_OPTIONS: CookieOptions = {
  path: '/',
  secure: false,
  sameSite: 'lax',
  httpOnly: false,
};

const readPageViews = (cookies: Record<string, string>) => {
  const parsed = Number.parseInt(cookies[appBannerPageViewsCookieName] ?? '', 10);

  return Number.isNaN(parsed) ? 0 : parsed;
};

export const useAppBannerVisibility = () => {
  const { getCookies, setCookie } = useCookies();
  const {
    appBanner: { dismissDurationDays, autoHideEnabled, autoHidePageViews },
  } = useConfig();
  const pageviewId = usePageviewId();
  const { isHydrated } = useIsHydrated();

  const [isHidden, setIsHidden] = useState(() => getCookies()[appBannerCookieName] === 'true');
  const [pageViews, setPageViews] = useState(() => readPageViews(getCookies()));
  const [countedPageviewId, setCountedPageviewId] = useState<string>();

  const persistDismissal = useCallback(() => {
    setCookie(null, appBannerCookieName, 'true', {
      ...BASE_COOKIE_OPTIONS,
      maxAge: dismissDurationDays * SECONDS_PER_DAY,
    });
  }, [dismissDurationDays, setCookie]);

  const hide = useCallback(() => {
    setIsHidden(true);
    persistDismissal();
  }, [persistDismissal]);

  // Not during hydration: without an initial pageview id the server counts nothing, and the
  // hydration render has to match its markup.
  if (
    isHydrated &&
    autoHideEnabled &&
    pageviewId &&
    !isHidden &&
    countedPageviewId !== pageviewId
  ) {
    const nextPageViews = readPageViews(getCookies()) + 1;
    setCountedPageviewId(pageviewId);
    setPageViews(nextPageViews);
    if (nextPageViews > autoHidePageViews) {
      setIsHidden(true);
    }
  }

  useEffect(() => {
    if (countedPageviewId === undefined) return;

    setCookie(null, appBannerPageViewsCookieName, String(pageViews), BASE_COOKIE_OPTIONS);
    if (pageViews > autoHidePageViews) {
      persistDismissal();
    }
  }, [countedPageviewId, pageViews, autoHidePageViews, persistDismissal, setCookie]);

  const isAutoHidden = autoHideEnabled && pageViews > autoHidePageViews;

  return { isVisible: !isHidden && !isAutoHidden, dismiss: hide };
};
